/**
=========================================================
* Elite 247 Admin Panel - Payment Management
=========================================================
*/

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDInput from "components/MDInput";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";
import { lightDark, DARK } from "utils/lightDark";

// Deep purple / red icons disappear on the dark card; lighter shades in dark mode.
const HISTORY_ICON = lightDark("#9c27b0", "#ce93d8");

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount, currency) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(amount || 0);
}

const STATUS_COLORS = {
  paid: "#4caf50",
  succeeded: "#2196f3",
  pending: "#ff9800",
  active: "#ff9800",
  partially_paid: "#03a9f4",
  failed: "#f44336",
  refunded: "#9c27b0",
  expired: "#757575",
  cancelled: "#757575",
  disputed: "#e91e63",
};

const STATUS_ICONS = {
  paid: "check_circle",
  succeeded: "verified",
  pending: "schedule",
  active: "schedule",
  partially_paid: "incomplete_circle",
  failed: "error",
  refunded: "replay",
  expired: "access_time",
  cancelled: "cancel",
  disputed: "gavel",
};

function statusColor(status) {
  return STATUS_COLORS[status] || "#757575";
}

function statusIcon(status) {
  return STATUS_ICONS[status] || "help";
}

// Resolve timestamp from either ledger events (occurredAt) or legacy events (date)
function eventDate(event) {
  return event.occurredAt || event.date;
}

// Below md the table becomes stacked cards: give row actions a ~40px tap target and small
// buttons the regular 40px height (theme small = 2rem), leaving the desktop sizes as they were.
const ROW_ACTION_SX = { width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } };
const SMALL_BUTTON_TAP_SX = { minHeight: { xs: "2.5rem", md: "2rem" } };

// The theme's Select override strips its vertical padding, so Selects render ~20px tall.
// Where the filters stack full-width (below md), match the 37px small TextField height
// (doubled class so it outranks the Select's own later-inserted min-height rule).
const selectTapSx = (theme) => ({
  [theme.breakpoints.down("md")]: { "& .MuiSelect-select.MuiInputBase-input": { minHeight: 37 } },
});

// The theme gives phone dialogs a 12px inset (with !important, inside a max-width media query);
// undo it, in the same media query so it wins, for the dialogs that go fullScreen there.
const fullScreenPaperSx = (theme) => ({
  [theme.breakpoints.down("sm")]: {
    m: 0,
    width: "100%",
    maxWidth: "100% !important",
    maxHeight: "none",
  },
});

// Paystack bootcamp payments end as "succeeded" (Stripe ones as "paid"): both are paid.
export function matchesStatusFilter(p, filterStatus) {
  if (filterStatus === "paid") return p.paymentStatus === "paid" || p.paymentStatus === "succeeded";
  return p.paymentStatus === filterStatus;
}

// Mirrors the server: a partial refund leaves a payment Paid (or Succeeded) so the rest can be
// refunded; it closes (Refunded / Cancelled) only once everything is refunded. Flexible links
// can be refunded as soon as money has come in, not only when fully paid.
export function isRefundable(p) {
  const statusOk =
    p.paymentStatus === "paid" ||
    p.paymentStatus === "succeeded" ||
    (p.paymentType === "flexible" && p.paymentStatus === "partially_paid");
  return statusOk && (p.refundedAmount || 0) < (p.amountPaid || 0) - 0.005;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function PaymentManagement() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState("");
  const [globalSuccess, setGlobalSuccess] = useState("");

  // Dialog open states
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState({ timeline: [], source: "legacy" });

  // Filter states
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Refund form state
  const [refundType, setRefundType] = useState("full"); // "full" | "partial"
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundStep, setRefundStep] = useState(1); // 1 = form, 2 = confirm
  const [refundError, setRefundError] = useState("");
  const [processing, setProcessing] = useState(false);

  // Cancelling subscription state
  const [cancellingId, setCancellingId] = useState(null);

  // Reconciliation state
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filterStatus, filterMethod, filterSource, searchTerm, payments]);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getPayments();
      setPayments(data);
      setGlobalError("");
    } catch (err) {
      setGlobalError(err.response?.data?.message || "Failed to fetch payments");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await adminApi.getPaymentStats();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const handleCancelSubscription = async (payment) => {
    setCancellingId(payment._id);
    setGlobalError("");
    setGlobalSuccess("");
    try {
      await adminApi.cancelLinkSubscription(payment._id);
      setGlobalSuccess(`Subscription cancelled for ${payment.name}.`);
      await fetchPayments();
    } catch (err) {
      setGlobalError(err?.response?.data?.error || err?.message || "Failed to cancel subscription");
    } finally {
      setCancellingId(null);
    }
  };

  // Catches up any instalment cycles a missed/delayed webhook failed to record, and
  // lets the auto-cancel-once-fully-paid logic fire retroactively for plans that fell behind.
  const handleReconcileSubscriptions = async () => {
    setReconciling(true);
    setGlobalError("");
    setGlobalSuccess("");
    try {
      const result = await adminApi.reconcileSubscriptions();
      setGlobalSuccess(
        `Reconciliation complete — ${result.checked} plan(s) checked, ${result.cyclesRecovered} cycle(s) recovered, ` +
          `${result.completed} plan(s) completed${
            result.overcollected ? `, ${result.overcollected} overcollection flag(s) raised` : ""
          }.`
      );
      await fetchPayments(); // clears the error banner, so the failures are shown after it
      // Plans the run couldn't check (gateway errors) come back in `errors`; name them.
      const failures = result.errors || [];
      if (failures.length) {
        const detail = failures.map((f) => {
          const name = payments.find((p) => p._id === f.linkId)?.name || f.linkId;
          return `${name}: ${f.error}`;
        });
        setGlobalError(`${failures.length} plan(s) could not be reconciled: ${detail.join("; ")}`);
      }
    } catch (err) {
      setGlobalError(err?.response?.data?.error || err?.message || "Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...payments];

    if (filterSource) filtered = filtered.filter((p) => p.source === filterSource);
    if (filterStatus) filtered = filtered.filter((p) => matchesStatusFilter(p, filterStatus));
    if (filterMethod) filtered = filtered.filter((p) => p.paymentMethod === filterMethod);

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name?.toLowerCase().includes(term) ||
          p.email?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.paystackReference?.toLowerCase().includes(term) ||
          p.stripeSessionId?.toLowerCase().includes(term)
      );
    }

    setFilteredPayments(filtered);
  };

  // ---------------------------------------------------------------------------
  // Dialog handlers
  // ---------------------------------------------------------------------------

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setOpenDetailsDialog(true);
  };

  const handleViewHistory = async (payment) => {
    try {
      setSelectedPayment(payment);
      const data =
        payment.source === "generated_link"
          ? await adminApi.getGeneratedLinkHistory(payment._id)
          : await adminApi.getPaymentHistory(payment._id);
      setPaymentHistory(data);
      setOpenHistoryDialog(true);
    } catch (err) {
      setGlobalError("Failed to fetch payment history");
    }
  };

  const handleOpenRefundDialog = (payment) => {
    const alreadyRefunded = payment.refundedAmount || 0;
    const remaining = payment.amountPaid - alreadyRefunded;

    setSelectedPayment(payment);
    setRefundType("full");
    setRefundAmount(remaining.toFixed(2));
    setRefundReason("");
    setRefundStep(1);
    setRefundError("");
    setOpenRefundDialog(true);
  };

  const handleCloseRefundDialog = () => {
    if (processing) return;
    setOpenRefundDialog(false);
  };

  // Switch between full/partial and keep amount in sync
  const handleRefundTypeChange = (_, newType) => {
    if (!newType) return; // ToggleButtonGroup requires a value
    const alreadyRefunded = selectedPayment?.refundedAmount || 0;
    const remaining = (selectedPayment?.amountPaid || 0) - alreadyRefunded;

    setRefundType(newType);
    if (newType === "full") setRefundAmount(remaining.toFixed(2));
    else setRefundAmount("");
    setRefundError("");
  };

  // Step 1 → 2: validate then show confirmation summary
  const handleReviewRefund = () => {
    setRefundError("");

    const amount = parseFloat(refundAmount);
    const alreadyRefunded = selectedPayment?.refundedAmount || 0;
    const maxRefundable = (selectedPayment?.amountPaid || 0) - alreadyRefunded;

    if (!refundReason.trim()) {
      setRefundError("A reason is required before processing a refund.");
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      setRefundError("Enter a valid refund amount greater than zero.");
      return;
    }
    if (amount > maxRefundable + 0.001) {
      setRefundError(
        `Refund amount cannot exceed the remaining refundable amount of ${formatCurrency(
          maxRefundable,
          selectedPayment?.currency
        )}.`
      );
      return;
    }

    setRefundStep(2);
  };

  // Step 2: submit
  const handleConfirmRefund = async () => {
    try {
      setProcessing(true);
      setRefundError("");

      const refundFn =
        selectedPayment.source === "generated_link"
          ? adminApi.refundGeneratedPayment
          : adminApi.refundPayment;

      const result = await refundFn(selectedPayment._id, {
        amount: parseFloat(refundAmount),
        reason: refundReason.trim(),
      });

      setOpenRefundDialog(false);
      setGlobalSuccess(
        `Refund of ${formatCurrency(
          parseFloat(refundAmount),
          selectedPayment?.currency
        )} processed successfully.`
      );
      // The gateway refunded but the record could not be fully updated: say so.
      setGlobalError(result?.warning || "");
      fetchPayments();
      fetchStats();
    } catch (err) {
      // Surface the error inside the dialog so it's visible while it's open
      setRefundError(err.response?.data?.error || err.message || "Failed to process refund.");
      setRefundStep(1); // Take user back to form to correct the issue
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryPayment = async (payment) => {
    try {
      setProcessing(true);
      setGlobalError("");
      setGlobalSuccess("");

      const result = await adminApi.retryPayment(payment._id);
      if (payment.source === "generated_link") {
        // An expired link is re-issued: same URL, a new expiry, and the email + SMS sent
        // again. Opening it here would log a "Customer Viewed Link" visit, so it isn't.
        const until = result.expiresAt
          ? new Date(result.expiresAt).toLocaleDateString("en-GB")
          : "";
        setGlobalSuccess(
          `Link re-issued for ${payment.name}${until ? `; it now expires ${until}` : ""}. ` +
            `Email ${result.emailSent ? "✓" : "✗"}. SMS ${result.smsSent ? "✓" : "✗"}.`
        );
        fetchStats();
      } else {
        window.open(result.paymentLink, "_blank");
        setGlobalSuccess("Payment retry link generated and opened in a new tab.");
      }
      fetchPayments();
    } catch (err) {
      setGlobalError(err.response?.data?.error || "Failed to create retry link");
    } finally {
      setProcessing(false);
    }
  };

  const handleExport = async () => {
    try {
      setProcessing(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterMethod) params.method = filterMethod;

      const blob = await adminApi.exportPayments(params);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `payments-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setGlobalSuccess("Payment data exported successfully.");
    } catch (err) {
      setGlobalError("Failed to export payment data");
    } finally {
      setProcessing(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Table definition
  // ---------------------------------------------------------------------------

  const columns = [
    { Header: "source", accessor: "source", width: "10%", align: "center" },
    // Name and email share one column so the 8-column table fits a laptop-width card.
    { Header: "customer", accessor: "customer", width: "26%", align: "left", breakAnywhere: true },
    { Header: "amount", accessor: "amount", width: "11%", align: "right" },
    { Header: "method", accessor: "method", width: "9%", align: "center" },
    { Header: "status", accessor: "status", width: "12%", align: "center" },
    { Header: "date", accessor: "date", width: "10%", align: "left" },
    { Header: "actions", accessor: "actions", width: "18%", align: "center" },
  ];

  const rows = filteredPayments.map((payment) => ({
    source: (
      <Chip
        label={payment.source === "generated_link" ? "Pay Link" : "Bootcamp"}
        size="small"
        sx={{
          backgroundColor: payment.source === "generated_link" ? "#1b5e20" : "#0d47a1",
          color: "white",
          fontSize: "0.6rem",
          fontWeight: "bold",
        }}
      />
    ),
    customer: (
      <MDBox>
        <MDTypography variant="caption" fontWeight="medium" display="block">
          {payment.name || "N/A"}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block">
          {payment.email || "N/A"}
        </MDTypography>
      </MDBox>
    ),
    amount: (
      <MDBox textAlign={{ xs: "left", md: "right" }}>
        <MDTypography variant="caption" fontWeight="medium">
          {formatCurrency(payment.amountPaid, payment.currency)}
        </MDTypography>
        {payment.refundedAmount > 0 && (
          <MDTypography
            variant="caption"
            color="error"
            display="block"
            sx={{ fontSize: "0.65rem" }}
          >
            -{formatCurrency(payment.refundedAmount, payment.currency)} refunded
          </MDTypography>
        )}
      </MDBox>
    ),
    method: (
      <Chip
        label={payment.paymentMethod || "N/A"}
        size="small"
        sx={{
          backgroundColor: payment.paymentMethod === "stripe" ? "#635BFF" : "#00C3F7",
          color: "white",
          fontWeight: "bold",
          textTransform: "capitalize",
        }}
      />
    ),
    status: (
      <Tooltip title={`Status: ${payment.paymentStatus}`} placement="top">
        <Chip
          icon={
            <Icon sx={{ fontSize: "16px !important" }}>{statusIcon(payment.paymentStatus)}</Icon>
          }
          label={payment.paymentStatus}
          size="small"
          sx={{
            backgroundColor: statusColor(payment.paymentStatus),
            color: "white",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        />
      </Tooltip>
    ),
    // Plain text inherits the table cell's font size; in the mobile card it would inherit the
    // (larger) body size instead, so pin it to caption size below md. ("100%", not "inherit":
    // sx resolves fontSize against theme.typography, which has an "inherit" variant object.)
    date: (
      <MDBox component="span" color="text" sx={{ fontSize: { xs: "0.75rem", md: "100%" } }}>
        {payment.paidAt
          ? new Date(payment.paidAt).toLocaleDateString()
          : new Date(payment.createdAt).toLocaleDateString()}
      </MDBox>
    ),
    actions: (
      <MDBox display="flex" gap={{ xs: 1, md: 0.5 }} justifyContent="center">
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={() => handleViewDetails(payment)}
            sx={{ color: "#1976d2", ...ROW_ACTION_SX }}
          >
            <Icon>visibility</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="View history">
          <IconButton
            size="small"
            onClick={() => handleViewHistory(payment)}
            sx={{ color: HISTORY_ICON, ...ROW_ACTION_SX }}
          >
            <Icon>history</Icon>
          </IconButton>
        </Tooltip>
        {isRefundable(payment) && (
          <Tooltip title="Refund payment">
            <IconButton
              size="small"
              onClick={() => handleOpenRefundDialog(payment)}
              sx={{ color: "#f44336", ...ROW_ACTION_SX }}
            >
              <Icon>replay</Icon>
            </IconButton>
          </Tooltip>
        )}
        {(payment.paymentStatus === "failed" || payment.paymentStatus === "expired") && (
          <Tooltip
            title={
              payment.source === "generated_link"
                ? "Re-issue link: new expiry, email & SMS sent again"
                : "Retry payment"
            }
          >
            <IconButton
              size="small"
              onClick={() => handleRetryPayment(payment)}
              sx={{ color: "#4caf50", ...ROW_ACTION_SX }}
            >
              <Icon>refresh</Icon>
            </IconButton>
          </Tooltip>
        )}
        {payment.source === "generated_link" &&
          payment.paymentType === "installment" &&
          payment.subscriptionStatus === "active" && (
            <Tooltip title="Cancel subscription — stop future auto-billed instalments">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleCancelSubscription(payment)}
                  disabled={cancellingId === payment._id}
                  sx={{ color: lightDark("#b71c1c", "#ef9a9a"), ...ROW_ACTION_SX }}
                >
                  {cancellingId === payment._id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon>block</Icon>
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
      </MDBox>
    ),
  }));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const maxRefundable = selectedPayment
    ? (selectedPayment.amountPaid || 0) - (selectedPayment.refundedAmount || 0)
    : 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Statistics */}
        {stats && (
          <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 5, md: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <Icon fontSize="large" color="success">
                    payments
                  </Icon>
                  <MDTypography variant="caption" color="text" display="block" mt={1} mb={0.5}>
                    Revenue (by currency)
                  </MDTypography>
                  {Object.keys(stats.revenueByurrency || {}).length === 0 ? (
                    <MDTypography variant="h5" fontWeight="bold">
                      —
                    </MDTypography>
                  ) : (
                    Object.entries(stats.revenueByurrency || {})
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([currency, amount]) => (
                        <MDTypography
                          key={currency}
                          variant="h5"
                          fontWeight="bold"
                          lineHeight={1.4}
                        >
                          {formatCurrency(amount, currency)}
                        </MDTypography>
                      ))
                  )}
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={4} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <Icon fontSize="large" color="info">
                    check_circle
                  </Icon>
                  <MDTypography variant="h4" fontWeight="bold" mt={1}>
                    {stats.byStatus?.find((s) => s._id === "paid")?.count || 0}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Paid
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={4} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <Icon fontSize="large" color="warning">
                    schedule
                  </Icon>
                  <MDTypography variant="h4" fontWeight="bold" mt={1}>
                    {stats.byStatus?.find((s) => s._id === "pending")?.count || 0}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Pending
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={4} md={3}>
              <Card>
                <MDBox p={2} textAlign="center">
                  <Icon fontSize="large" color="error">
                    error
                  </Icon>
                  <MDTypography variant="h4" fontWeight="bold" mt={1}>
                    {stats.byStatus?.find((s) => s._id === "failed")?.count || 0}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Failed
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* ── All Payments ───────────────────────────────────────────────── */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={{ xs: 2, md: 3 }}
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "lg",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
                }}
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    Payment Management
                  </MDTypography>
                  {/* On phones the buttons wrap onto their own full-width rows */}
                  <MDBox display="flex" flexWrap="wrap" gap={1} width={{ xs: "100%", sm: "auto" }}>
                    <Tooltip title="Catch up any instalment cycles a missed webhook failed to record, and auto-cancel subscriptions that turn out to be fully paid">
                      <MDBox component="span" flex={{ xs: "1 1 auto", sm: "0 1 auto" }}>
                        <MDButton
                          variant="outlined"
                          color="white"
                          size="small"
                          startIcon={
                            reconciling ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <Icon>sync</Icon>
                            )
                          }
                          onClick={handleReconcileSubscriptions}
                          disabled={reconciling}
                          sx={{
                            ...SMALL_BUTTON_TAP_SX,
                            width: { xs: "100%", sm: "auto" },
                            whiteSpace: "nowrap",
                          }}
                        >
                          {reconciling ? "Reconciling…" : "Reconcile Subscriptions"}
                        </MDButton>
                      </MDBox>
                    </Tooltip>
                    <MDButton
                      variant="contained"
                      color="white"
                      size="small"
                      startIcon={<Icon>download</Icon>}
                      onClick={handleExport}
                      disabled={processing}
                      sx={{
                        ...SMALL_BUTTON_TAP_SX,
                        flex: { xs: "1 1 auto", sm: "0 1 auto" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      Export CSV
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox p={{ xs: 2, md: 3 }}>
                {/* Filters */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      placeholder="Name, email, description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{ startAdornment: <Icon sx={{ mr: 1 }}>search</Icon> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={filterSource}
                        label="Source"
                        onChange={(e) => setFilterSource(e.target.value)}
                        sx={selectTapSx}
                      >
                        <MenuItem value="">All Sources</MenuItem>
                        <MenuItem value="bootcamp">Bootcamp</MenuItem>
                        <MenuItem value="generated_link">Payment Links</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterStatus}
                        label="Status"
                        onChange={(e) => setFilterStatus(e.target.value)}
                        sx={selectTapSx}
                      >
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="partially_paid">Partially Paid</MenuItem>
                        <MenuItem value="pending">Pending / Active</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Gateway</InputLabel>
                      <Select
                        value={filterMethod}
                        label="Gateway"
                        onChange={(e) => setFilterMethod(e.target.value)}
                        sx={selectTapSx}
                      >
                        <MenuItem value="">All Gateways</MenuItem>
                        <MenuItem value="stripe">Stripe</MenuItem>
                        <MenuItem value="paystack">Paystack</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {globalError && (
                  <MDBox mb={2}>
                    <MDAlert color="error" dismissible onClose={() => setGlobalError("")}>
                      {globalError}
                    </MDAlert>
                  </MDBox>
                )}
                {globalSuccess && (
                  <MDBox mb={2}>
                    <MDAlert color="success" dismissible onClose={() => setGlobalSuccess("")}>
                      {globalSuccess}
                    </MDAlert>
                  </MDBox>
                )}

                <MDBox mt={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                    canSearch={false}
                    resetPageKey={`${searchTerm}|${filterSource}|${filterStatus}|${filterMethod}`}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ------------------------------------------------------------------ */}
      {/* Payment Details Dialog                                              */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{ sx: isPhone ? fullScreenPaperSx : undefined }}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDTypography variant="h5" fontWeight="bold">
              Payment Details
            </MDTypography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Name
                </MDTypography>
                <MDTypography variant="body2">{selectedPayment.name}</MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Email
                </MDTypography>
                <MDTypography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  {selectedPayment.email}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Amount Paid
                </MDTypography>
                <MDTypography variant="body2">
                  {formatCurrency(selectedPayment.amountPaid, selectedPayment.currency)}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Payment Method
                </MDTypography>
                <MDTypography variant="body2" textTransform="capitalize">
                  {selectedPayment.paymentMethod || "N/A"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Payment Status
                </MDTypography>
                <MDBox mt={0.5}>
                  <Chip
                    label={selectedPayment.paymentStatus}
                    size="small"
                    sx={{
                      backgroundColor: statusColor(selectedPayment.paymentStatus),
                      color: "white",
                    }}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Currency
                </MDTypography>
                <MDTypography variant="body2">{selectedPayment.currency}</MDTypography>
              </Grid>
              {selectedPayment.source === "generated_link" && selectedPayment.description && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Description
                  </MDTypography>
                  <MDTypography variant="body2">{selectedPayment.description}</MDTypography>
                </Grid>
              )}
              {selectedPayment.source === "generated_link" && (
                <>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Payment Type
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ textTransform: "capitalize" }}>
                      {selectedPayment.paymentType || "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Notifications
                    </MDTypography>
                    <MDTypography variant="body2">
                      {[selectedPayment.emailSent && "Email", selectedPayment.smsSent && "SMS"]
                        .filter(Boolean)
                        .join(", ") || "None sent"}
                    </MDTypography>
                  </Grid>
                  {selectedPayment.phone && (
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        Phone
                      </MDTypography>
                      <MDTypography variant="body2">{selectedPayment.phone}</MDTypography>
                    </Grid>
                  )}
                </>
              )}
              {selectedPayment.stripeSessionId && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Stripe Session ID
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                  >
                    {selectedPayment.stripeSessionId}
                  </MDTypography>
                </Grid>
              )}
              {selectedPayment.stripePaymentIntentId && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Stripe Payment Intent
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                  >
                    {selectedPayment.stripePaymentIntentId}
                  </MDTypography>
                </Grid>
              )}
              {selectedPayment.paystackReference && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Paystack Reference
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {selectedPayment.paystackReference}
                  </MDTypography>
                </Grid>
              )}
              {selectedPayment.refundedAmount > 0 && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Refunded Amount
                  </MDTypography>
                  <MDTypography variant="body2" color="error">
                    {formatCurrency(selectedPayment.refundedAmount, selectedPayment.currency)}
                  </MDTypography>
                </Grid>
              )}
              {selectedPayment.failureReason && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Failure Reason
                  </MDTypography>
                  <MDTypography variant="body2" color="error">
                    {selectedPayment.failureReason}
                  </MDTypography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="bold">
                  Created At
                </MDTypography>
                <MDTypography variant="body2">
                  {new Date(selectedPayment.createdAt).toLocaleString()}
                </MDTypography>
              </Grid>
              {selectedPayment.paidAt && (
                <Grid item xs={12} md={6}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Paid At
                  </MDTypography>
                  <MDTypography variant="body2">
                    {new Date(selectedPayment.paidAt).toLocaleString()}
                  </MDTypography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPayment && isRefundable(selectedPayment) && (
            <MDButton
              color="error"
              size="small"
              startIcon={<Icon>replay</Icon>}
              onClick={() => {
                setOpenDetailsDialog(false);
                handleOpenRefundDialog(selectedPayment);
              }}
              sx={SMALL_BUTTON_TAP_SX}
            >
              Refund
            </MDButton>
          )}
          <MDButton onClick={() => setOpenDetailsDialog(false)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Refund Dialog                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={openRefundDialog}
        onClose={handleCloseRefundDialog}
        maxWidth="sm"
        fullWidth
        // Prevent accidental close during processing
        disableEscapeKeyDown={processing}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "#f44336" }}>replay</Icon>
            <MDTypography variant="h5" fontWeight="bold">
              {refundStep === 1 ? "Refund Payment" : "Confirm Refund"}
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent dividers>
          {selectedPayment && refundStep === 1 && (
            <MDBox>
              {/* Error inside dialog so it's visible while the dialog is open */}
              {refundError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefundError("")}>
                  {refundError}
                </Alert>
              )}

              {/* Amount breakdown */}
              <MDBox
                sx={{
                  background: lightDark("#f5f5f5", DARK.surface),
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Original charge
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="bold">
                      {formatCurrency(selectedPayment.amountPaid, selectedPayment.currency)}
                    </MDTypography>
                  </Grid>
                  {(selectedPayment.refundedAmount || 0) > 0 && (
                    <Grid item xs={6}>
                      <MDTypography variant="caption" color="text">
                        Already refunded
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="bold" color="error">
                        -{formatCurrency(selectedPayment.refundedAmount, selectedPayment.currency)}
                      </MDTypography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <MDTypography variant="caption" color="text">
                      Remaining refundable
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="bold" color="success">
                      {formatCurrency(maxRefundable, selectedPayment.currency)}
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Full / Partial toggle */}
              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  display="block"
                  mb={1}
                >
                  Refund type
                </MDTypography>
                <ToggleButtonGroup
                  value={refundType}
                  exclusive
                  onChange={handleRefundTypeChange}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="full" sx={{ minHeight: { xs: 40, sm: "auto" } }}>
                    Full refund
                  </ToggleButton>
                  <ToggleButton value="partial" sx={{ minHeight: { xs: 40, sm: "auto" } }}>
                    Partial refund
                  </ToggleButton>
                </ToggleButtonGroup>
              </MDBox>

              {/* Amount field — read-only for full, editable for partial */}
              <MDBox mb={2}>
                <MDInput
                  type="number"
                  label={`Refund amount (${selectedPayment.currency})`}
                  fullWidth
                  value={refundAmount}
                  onChange={(e) => {
                    setRefundAmount(e.target.value);
                    setRefundError("");
                  }}
                  disabled={refundType === "full"}
                  inputProps={{
                    step: "0.01",
                    min: "0.01",
                    max: maxRefundable,
                  }}
                  helperText={
                    refundType === "full"
                      ? "Full remaining balance will be refunded"
                      : `Maximum: ${formatCurrency(maxRefundable, selectedPayment.currency)}`
                  }
                />
              </MDBox>

              {/* Reason — required */}
              <TextField
                label="Reason for refund *"
                fullWidth
                multiline
                rows={3}
                value={refundReason}
                onChange={(e) => {
                  setRefundReason(e.target.value);
                  setRefundError("");
                }}
                placeholder="Describe why this refund is being issued..."
                helperText="Required. This is recorded in the payment ledger and sent to the payment provider."
              />
            </MDBox>
          )}

          {/* Step 2 — confirmation summary */}
          {selectedPayment && refundStep === 2 && (
            <MDBox>
              {refundError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefundError("")}>
                  {refundError}
                </Alert>
              )}

              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>This action cannot be undone.</strong> The refund will be submitted to{" "}
                {selectedPayment.paymentMethod} immediately.
              </Alert>

              <MDBox sx={{ background: lightDark("#f5f5f5", DARK.surface), borderRadius: 2, p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Customer
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="bold">
                      {selectedPayment.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ overflowWrap: "anywhere" }}>
                      {selectedPayment.email}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Refund amount
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="error">
                      {formatCurrency(parseFloat(refundAmount), selectedPayment.currency)}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text">
                      Reason
                    </MDTypography>
                    <MDTypography variant="body2">{refundReason}</MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Gateway
                    </MDTypography>
                    <MDTypography variant="body2" textTransform="capitalize">
                      {selectedPayment.paymentMethod}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text">
                      Type
                    </MDTypography>
                    <MDTypography variant="body2" textTransform="capitalize">
                      {refundType}
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {refundStep === 1 && (
            <>
              <MDButton onClick={handleCloseRefundDialog} disabled={processing}>
                Cancel
              </MDButton>
              <MDButton color="error" onClick={handleReviewRefund} disabled={processing}>
                Review Refund
              </MDButton>
            </>
          )}

          {refundStep === 2 && (
            <>
              <MDButton
                onClick={() => {
                  setRefundStep(1);
                  setRefundError("");
                }}
                disabled={processing}
              >
                Go Back
              </MDButton>
              <MDButton
                color="error"
                onClick={handleConfirmRefund}
                disabled={processing}
                startIcon={
                  processing ? <CircularProgress size={16} color="inherit" /> : <Icon>replay</Icon>
                }
              >
                {processing ? "Processing…" : "Confirm & Refund"}
              </MDButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Payment History Dialog                                              */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{ sx: isPhone ? fullScreenPaperSx : undefined }}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <Icon sx={{ color: HISTORY_ICON }}>history</Icon>
              <MDTypography variant="h5" fontWeight="bold">
                Payment History
              </MDTypography>
              {paymentHistory.source === "ledger" && (
                <Chip
                  label="Live ledger"
                  size="small"
                  sx={{ backgroundColor: "#4caf50", color: "white", ml: 1 }}
                />
              )}
              {paymentHistory.source === "legacy" && (
                <Chip
                  label="Legacy record"
                  size="small"
                  sx={{ backgroundColor: "#ff9800", color: "white", ml: 1 }}
                />
              )}
            </MDBox>
            <IconButton onClick={() => setOpenHistoryDialog(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          {paymentHistory.timeline?.length > 0 ? (
            // Phones: one column (alternating sides is too cramped at ~360px), with the
            // timestamp/provider moved under each event title instead of the opposite side.
            <Timeline
              position={isPhone ? "right" : "alternate"}
              sx={
                isPhone
                  ? { px: 0, "& .MuiTimelineItem-root::before": { flex: 0, p: 0 } }
                  : undefined
              }
            >
              {paymentHistory.timeline.map((event, index) => (
                <TimelineItem key={index}>
                  {!isPhone && (
                    <TimelineOppositeContent color="text.secondary">
                      <MDTypography variant="caption">
                        {new Date(eventDate(event)).toLocaleString()}
                      </MDTypography>
                      {event.provider && (
                        <MDTypography
                          variant="caption"
                          display="block"
                          sx={{ textTransform: "capitalize", opacity: 0.7 }}
                        >
                          {event.provider}
                        </MDTypography>
                      )}
                    </TimelineOppositeContent>
                  )}
                  <TimelineSeparator>
                    <TimelineDot sx={{ backgroundColor: statusColor(event.status) }}>
                      <Icon fontSize="small" sx={{ color: "white" }}>
                        {statusIcon(event.status)}
                      </Icon>
                    </TimelineDot>
                    {index < paymentHistory.timeline.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <MDTypography variant="subtitle2" fontWeight="bold">
                      {event.event}
                    </MDTypography>
                    {isPhone && (
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                        sx={{ opacity: 0.8 }}
                      >
                        {new Date(eventDate(event)).toLocaleString()}
                        {event.provider && (
                          <MDBox component="span" sx={{ textTransform: "capitalize" }}>
                            {" · "}
                            {event.provider}
                          </MDBox>
                        )}
                      </MDTypography>
                    )}
                    {event.amount != null && (
                      <MDTypography variant="caption" color="text" display="block">
                        {formatCurrency(event.amount, event.currency || selectedPayment?.currency)}
                      </MDTypography>
                    )}
                    {event.details && (
                      <MDTypography variant="caption" color="text">
                        {event.details}
                      </MDTypography>
                    )}
                    {event.metadata?.failureReason && (
                      <MDTypography variant="caption" color="error" display="block">
                        {event.metadata.failureReason}
                      </MDTypography>
                    )}
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          ) : (
            <MDBox textAlign="center" py={4}>
              <Icon fontSize="large" color="disabled">
                history
              </Icon>
              <MDTypography variant="body2" color="text" mt={2}>
                No payment history available
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default PaymentManagement;
